import React, { ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { GET_ENTITY_CONVERSATION_THREADS } from '../../../utils/apollo/conversations';
import {
  IConversationEntityInput,
  IConversationThreadFieldsFragment,
  IGetEntityConversationThreadsQuery,
  IGetEntityConversationThreadsQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  populateThreads,
  resetConversationThreads,
  setActiveThread,
} from '../../../redux/slices/conversationThreads';
import { theme } from '../../../styles/theme';
import ScrollableWrapper from '../../ScrollableWrapper';
import { KubitLogoLoader } from '../../Chart/ChartLoader';
import ConversationThread from './ConversationThread';
import EmptyConversationThreads from './EmptyConversationThreads';

interface IProps {
  ids: IConversationEntityInput[];
  settingsComponent?: ReactNode;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;
const Header = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
  padding: 16px;
  border-bottom: 1px solid ${theme.colors.border};
`;
const Heading = styled.h3`
  margin: 0;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Poll conversations each 2 minutes
const POLL_INTERVAL = 120000;

const ConversationThreads = ({ ids, settingsComponent }: IProps) => {
  const dispatch = useAppDispatch();

  const threads = useAppSelector((state) => state.conversationThreads.threads);
  const activeThread = useAppSelector(
    (state) => state.conversationThreads.activeThread,
  );

  const handleLoadThreads = ({
    entityConversationThreads,
  }: IGetEntityConversationThreadsQuery) => {
    // populate ONLY unresolved threads
    dispatch(
      populateThreads(
        entityConversationThreads.filter((thread) => !thread.isResolved),
      ),
    );
  };

  const { loading } = useQuery<
    IGetEntityConversationThreadsQuery,
    IGetEntityConversationThreadsQueryVariables
  >(GET_ENTITY_CONVERSATION_THREADS, {
    variables: {
      ids,
    },
    skip: !ids.length,
    onCompleted: handleLoadThreads,
    pollInterval: POLL_INTERVAL,
    notifyOnNetworkStatusChange: true,
  });

  const handleThreadClick = (thread: IConversationThreadFieldsFragment) => {
    dispatch(setActiveThread(thread));
  };

  useEffect(() => {
    return () => {
      // clean-up conversations
      dispatch(resetConversationThreads());
    };
  }, [dispatch]);

  return (
    <Container>
      <Header>
        <Heading>Comments</Heading>
        {settingsComponent}
      </Header>
      <ScrollableWrapper>
        <Content>
          {loading && !threads.length ? (
            <KubitLogoLoader height="100%" />
          ) : threads.length === 0 ? (
            <EmptyConversationThreads />
          ) : (
            <>
              {threads.map((thread) => (
                <ConversationThread
                  key={thread.id}
                  conversationThread={thread}
                  onClick={handleThreadClick}
                  isActive={activeThread?.id === thread.id}
                />
              ))}
            </>
          )}
        </Content>
      </ScrollableWrapper>
    </Container>
  );
};

export default ConversationThreads;
